<script lang="ts">
	import { Splide, SplideSlide } from '@splidejs/svelte-splide'
	import { executeSearch } from '$lib/js/search'
	import {
		storeBrandSearchFilter,
		storeCategorySearchFilter,
		storeSearchedText,
		storeSearchFilters,
		storeSearchModal,
	} from '$lib/js/stores'
	import { t } from 'svelte-i18n-lingui'
	import { is_set_array } from '$lib/js/helpers'
	import type { BaseProduct, FullProduct } from '$lib/js/elasticsearch/types'
	import { onMount } from 'svelte'
	import { page } from '$app/stores'
	import type { Suggestion } from '$lib/js/types'
	import { getContextScope } from '$lib/js/helpers/i18n'

	export let options: any = {}
	export let products: (BaseProduct | FullProduct)[] = []

	const scope = getContextScope()

	let input: HTMLElement
	let timeout_id: NodeJS.Timeout | null = null

	setTimeout(() => {
		if (input) { input.focus() }
	}, 700)

	const sliderOptions = {
		pagination: false,
		autoWidth: true,
		easing: 'cubic-bezier(0.37, 0, 0.63, 1)',
		breakpoints: {
			1024: {
				arrows: false,
			},
		},
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			closeSearch()
		}
	}

	function closeSearch() {
		$storeSearchModal = false
		$page.url.searchParams.delete('search')
		history.pushState({}, '', $page.url.href)
	}

	async function suggestionAction(suggestion: Suggestion) {
		if (suggestion.type == 'category') {
			$storeCategorySearchFilter = suggestion.obj
		}
		if (suggestion.type == 'brand') {
			$storeBrandSearchFilter = suggestion.obj
		}
		$storeSearchedText = ''
		const result = await executeSearch($storeSearchedText, {scope: scope})
		products = result?.products
		options = result?.options
	}

	function removeCategory() {
		$storeCategorySearchFilter = null
		products = []
		options = {}
	}

	function removeBrand() {
		$storeBrandSearchFilter = null
		products = []
		options = {}
	}

	async function doSearch() {
		const result = await executeSearch($storeSearchedText, {scope: scope})
		products = result?.products
		options = result?.options
	}

	$: if ($page.url.searchParams.get('search')) {
		doSearch()
	}



	// We use this function instead of pushState from sveltekit as it does not work and launches 2 times when pushing back button
	function doPush(url) {
		history.pushState({}, '', url)
		// console.log(url)
	}

	onMount(async () => {
		if ($storeCategorySearchFilter || $storeBrandSearchFilter) {
			const result = await executeSearch($storeSearchedText, {scope: scope})
			products = result?.products
			options = result?.options
		}
	})
</script>

<svelte:window on:keydown={handleKeydown} />

<div class="searchbar">
	<div class="searchbar__top">
		<div class="searchbar__bar">
			{#if $page.data.mobile === true || innerWidth <= 768}
				<span>
					<button class="close" on:click={() => closeSearch()}>
						<span class="back-arrow">←</span>
					</button>
				</span>
			{/if}
			<input
				type="text"
				bind:this={input}
				on:input={() => {
					if (timeout_id) {
						clearTimeout(timeout_id)
					}
					timeout_id = setTimeout(async () => {
						const result = await executeSearch($storeSearchedText, {scope: scope})
						products = result?.products
						options = result?.options
						// We add the search to history
						$page.url.searchParams.set('search', $storeSearchedText)
						doPush($page.url.href)
					}, 300)
				}}
				bind:value={$storeSearchedText}
				placeholder={$t`Search for a product...`}
			/>
			{#if $page.data.mobile === true || innerWidth <= 768}
				{#if $storeSearchedText.length > 0}
					<span
						class="empty-search-field"
						on:click={() => {
							$storeSearchedText = ''
						}}><span class="icon icon-cross"></span></span
					>
				{/if}
			{/if}
		</div>
		{#if $page.data.mobile !== true || innerWidth >= 768}
			<button class="close" on:click={() => closeSearch()}>
				<span class="icon icon-cross"></span>
			</button>
		{/if}
	</div>

	<div class="suggestions">
		{#if $storeSearchFilters.suggestions && is_set_array($storeSearchFilters.suggestions) && $storeSearchedText && $storeSearchedText.length > 2}
			<Splide options={sliderOptions} aria-label={'Suggestions slider'} class="small">
				{#each $storeSearchFilters.suggestions as suggestion}
					<SplideSlide>
						<a
							class="badge"
							on:click={() => {
								suggestionAction(suggestion)
							}}
						>
							<span class="icon icon-{suggestion.icon}" />{suggestion.name}
						</a>
					</SplideSlide>
				{/each}
			</Splide>
		{/if}
	</div>
	<div class="filters">
		<div>
			{#if $storeCategorySearchFilter}
				{$t`Searching in category:`}
				{$storeCategorySearchFilter.name}
				<span
					on:click={() => {
						removeCategory()
					}}><span class="icon icon-cross"></span></span
				>
			{/if}
		</div>
		<div>
			{#if $storeBrandSearchFilter}
				{$t`Searching in brand:`}
				{$storeBrandSearchFilter.page_title}
				<span
					on:click={() => {
						removeBrand()
					}}><span class="icon icon-cross"></span></span
				>
			{/if}
		</div>
	</div>
</div>

<style lang="scss">
	.empty-search-field {
		font-size: 15px;
		position: relative;
		left: -20px;
	}
	.filters span.icon {
		cursor: pointer;
	}
	@media (max-width: 720px) {
		.close-line {
			height: 40px;
		}
		.close {
			width: 45px;

			&span {
				font-size: 25px !important;
			}
		}
		.back-arrow {
			position: relative;
			top: 5px;
		}
		.filters {
			padding: 0px 0px 15px 15px;
		}
	}

	.searchbar {
		&__top {
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			gap: 20px;

			@media (min-width: 769px) {
				gap: 40px;
			}

			@media (max-width: 1024px) {
				padding: 0 0px;
			}

			.close {
				&:hover {
					color: var(--main);
				}

				span {
					font-size: 22px;
				}
			}
		}

		&__bar {
			flex-grow: 1;

			input {
				margin-bottom: 15px;
				height: 41px;
				font-size: 14px;
				border: none;

				border-bottom: 2px solid var(--grey--b);

				@media (min-width: 769px) {
					font-size: 16px;
					padding: 4px 15px 2px;
					width: calc(100% - 24px);
				}
				@media (max-width: 769px) {
					font-size: 16px;
					padding: 4px 15px 2px 15px;
					width: calc(100% - 104px);
				}

				&:focus {
					outline: none;
					border-color: var(--main);
				}
			}
		}

		.suggestions {
			padding-bottom: 15px;

			.badge {
				display: block;
				border: 1px solid var(--color-grey);
				border-radius: 15px;
				background: var(--color-grey);
				color: var(--color-grey--b);
				padding: 4px 15px;
				margin: 0 4px;

				&:hover {
					background: var(--main);
					border-color: var(--main);
					color: white;
				}

				@media (max-width: 768px) {
					font-size: 12px;
					padding: 4px 10px;
				}

				.icon {
					margin-right: 10px;
				}
			}
		}
	}
</style>
